import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
    LeftHeadDecoration,
    DetailRow,
    TxidLink
} from '../../../styles/main/MainCommonStyles'
import { DText, Flex, QrWrapper } from '../../../styles/CommonStyles'

import Text from '../../../core/utils/Text'
import { formatDate, formatNumber } from '../../../core/utils/common'
import { TradeTypeBadge } from '../../../styles/main/orders'
import { getTradeTypeColor } from '../../../core/utils/theme'
import { useMainContext } from '../../../core/contexts/main'

import ModalLayout from '../../../components/layout/main/ModalLayout'
import Operation from '../../../components/common/utils/Operation'
import useBankTransactions from '../../../core/hooks/main/bank-transactions/useBankTransactions'
import { useGetADueTransaction } from '../../../core/services/react-query/report/banking'
import TransactionModal from '../../../components/modals/TransactionModal'
import DetailsLayout from '../../../components/layout/main/DetailsLayout'
import styled from 'styled-components'
import QRCode from 'react-qr-code'
import { useCopyToClipboard } from '../../../core/hooks/common/useCopyToClipboard'
import { IoCopy } from 'react-icons/io5'
import RecipientDetails from '../../../components/main/reports/due-payments/recipients/RecipientDetails'

const DueTransactionDetails = () => {
    const params = useParams()
    const { lang } = useMainContext()
    const { copyToClip } = useCopyToClipboard()

    const { data: transaction, isFetching, isError, refetch } =
        useGetADueTransaction(params.detailId)


    const {
        hasWriteAccess,
        onModalClose,
        onOptionClicked,
        modal,
        getTransactionOptions
    } = useBankTransactions(true)

    const handleOnCloseModal = () => {
        refetch()
        onModalClose()
    }

    const fullName = transaction?.user
        ? `${transaction.user.firstName || ''} ${transaction.user.lastName || ''}`
        : ''

    const source = transaction?.meta?.source
    const destination = transaction?.meta?.destination
    const confirmation = destination?.confirmation
    const user = transaction?.user

    return (
        <>
            <DetailsLayout
                isFetching={isFetching}
                isError={isError}
                type={transaction?.status}
            >
                <LeftHeadDecoration left={'10px'}>
                    <div className='flex flex-col gap-1'>
                        <DText main>{params.detailId}</DText>

                        <DText primary>
                            Ref :
                            {' '}
                            {transaction?.refId}
                        </DText>
                    </div>
                </LeftHeadDecoration>

                <Flex fw justify='flex-end'>
                    <Operation
                        options={getTransactionOptions(!!transaction && transaction, false)}
                        hasWriteAccess={hasWriteAccess}
                        onOptionClicked={(idx) => onOptionClicked(idx, transaction)}
                    />
                </Flex>


                <DetailRow>
                    <DText main>User Email</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {user?.email || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Mobile</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {user?.mobile || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Country</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {user?.countryCode || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>User Status</DText>

                    <Flex justify='flex-end'>
                        <TradeTypeBadge color={getTradeTypeColor(user?.status)}>
                            {user?.status}
                        </TradeTypeBadge>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Short ID</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {user?.shortId}
                        </DText>
                    </Flex>
                </DetailRow>

                {/* USER */}
                <DetailRow>
                    <DText main>
                        <Text tid={'fullname'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>{fullName}</DText>
                    </Flex>
                </DetailRow>

                {/* FLOW */}
                <DetailRow>
                    <DText main>
                        <Text tid={'transaction-flow'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>
                            <Text tid={transaction?.flow} />
                        </DText>
                    </Flex>
                </DetailRow>

                {/* STATUS */}
                <DetailRow>
                    <DText main>
                        <Text tid={'transaction-status'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <TradeTypeBadge color={getTradeTypeColor(transaction?.status)}>
                            <Text tid={transaction?.status} />
                        </TradeTypeBadge>
                    </Flex>
                </DetailRow>

                {/* RAIL */}
                <DetailRow>
                    <DText main>
                        <Text tid={'rail'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>{transaction?.rail}</DText>
                    </Flex>
                </DetailRow>

                {/* CURRENCY */}
                <DetailRow>
                    <DText main>
                        <Text tid={'currency-in'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>{transaction?.currencyIn}</DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>
                        <Text tid={'currency-out'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>{transaction?.currencyOut}</DText>
                    </Flex>
                </DetailRow>

                {/* AMOUNT */}
                <DetailRow>
                    <DText main>
                        <Text tid={'amount'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>{formatNumber(transaction?.amount)}</DText>
                    </Flex>
                </DetailRow>

                {/* DESTINATION AMOUNT */}
                <DetailRow>
                    <DText main>
                        <Text tid={'destination-amount'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary>
                            {formatNumber(transaction?.meta?.destination?.amount)}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Source Fee</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {source?.fee || transaction?.feeIn} {transaction?.currencyIn?.toUpperCase()}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Destination Fee</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {destination?.fee || transaction?.feeOut} {transaction?.currencyOut?.toUpperCase()}
                        </DText>
                    </Flex>
                </DetailRow>

                {/* FX */}
                <DetailRow>
                    <DText main>FX Rate</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {transaction?.meta?.fxRate}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>FX Markup</DText>

                    <Flex justify='flex-end'>
                        <DText primary>
                            {transaction?.meta?.fxMarkup}%
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Reference ID</DText>

                    <Flex justify='flex-end' gap='8px'>
                        <DText primary lineBreak='anywhere'>
                            {transaction?.refId}
                        </DText>

                        <IoCopy
                            color='#fff'
                            size={18}
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyToClip(transaction?.refId)}
                        />
                    </Flex>
                </DetailRow>

                {/* WALLET */}
                <DetailRow>
                    <DText main>
                        <Text tid={'wallet-address'} />
                    </DText>
                    <Flex justify={'flex-end'}>
                        <DText primary lineBreak={'anywhere'}>
                            {transaction?.wallet?.details?.address}
                        </DText>
                    </Flex>
                </DetailRow>


                <DetailRow>
                    <DText main>Account ID</DText>

                    <Flex justify='flex-end' gap='8px'>
                        <DText primary lineBreak='anywhere'>
                            {transaction?.accountId}
                        </DText>

                        <IoCopy
                            color='#fff'
                            size={18}
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyToClip(transaction?.accountId)}
                        />
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Owner Account</DText>

                    <Flex justify='flex-end' gap='8px'>
                        <DText primary lineBreak='anywhere'>
                            {transaction?.meta?.ownerId}
                        </DText>

                        <IoCopy
                            color='#fff'
                            size={18}
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyToClip(transaction?.meta?.ownerId)}
                        />
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Recipient ID</DText>

                    <Flex justify='flex-end'>
                        <DText primary lineBreak='anywhere'>
                            {transaction?.recipientId || '-'}
                        </DText>
                    </Flex>
                </DetailRow>


                <DetailRow>
                    <DText main>Mongo ID</DText>

                    <Flex justify='flex-end' gap='8px'>
                        <DText primary lineBreak='anywhere'>
                            {transaction?._id}
                        </DText>

                        <IoCopy
                            color='#fff'
                            size={18}
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyToClip(transaction?._id)}
                        />
                    </Flex>
                </DetailRow>

                {
                    transaction?.purposeCode && (
                        <DetailRow>
                            <DText main>Purpose Code</DText>

                            <Flex justify='flex-end'>
                                <DText primary>
                                    {transaction.purposeCode}
                                </DText>
                            </Flex>
                        </DetailRow>
                    )
                }


                <DetailRow>
                    <DText main>Bank Name</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {source?.details?.bankName || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Account Holder</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {source?.details?.name || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Account Number</DText>

                    <Flex justify="flex-end" gap="8px">
                        <DText primary>
                            {source?.details?.accountNumber || '-'}
                        </DText>

                        {
                            source?.details?.accountNumber &&
                            <IoCopy
                                color='#fff'
                                size={18}
                                style={{ cursor: 'pointer' }}
                                onClick={() => copyToClip(source.details.accountNumber)}
                            />
                        }
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Branch</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {source?.details?.branchNumber || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Account Type</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {source?.details?.bankAccountType || '-'}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Institution ID</DText>

                    <Flex justify="flex-end" gap="8px">

                        <DText primary lineBreak="anywhere">
                            {source?.details?.financialInstitutionId || '-'}
                        </DText>

                        {
                            source?.details?.financialInstitutionId &&
                            <IoCopy
                                color='#fff'
                                size={18}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                    copyToClip(source.details.financialInstitutionId)
                                }
                            />
                        }

                    </Flex>
                </DetailRow>


                <DetailRow>
                    <DText main>Wallet Address</DText>

                    <Flex justify="flex-end" gap="8px">

                        <DText primary lineBreak="anywhere">
                            {destination?.details?.address || '-'}
                        </DText>

                        {
                            destination?.details?.address &&
                            <IoCopy
                                color='#fff'
                                size={18}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                    copyToClip(destination.details.address)
                                }
                            />
                        }

                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Wallet ID</DText>

                    <Flex justify="flex-end" gap="8px">

                        <DText primary lineBreak="anywhere">
                            {destination?.id}
                        </DText>

                        <IoCopy
                            color='#fff'
                            size={18}
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyToClip(destination?.id)}
                        />

                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Virtual Account</DText>

                    <Flex justify="flex-end" gap="8px">

                        <DText primary lineBreak="anywhere">
                            {destination?.virtualAccountId}
                        </DText>

                        <IoCopy
                            color='#fff'
                            size={18}
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                                copyToClip(destination?.virtualAccountId)
                            }
                        />

                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Network</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {confirmation?.network}
                        </DText>
                    </Flex>
                </DetailRow>

                <DetailRow>
                    <DText main>Rail</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {destination?.rail}
                        </DText>
                    </Flex>
                </DetailRow>


                <DetailRow>
                    <DText main>Schema</DText>

                    <Flex justify="flex-end">
                        <DText primary>
                            {destination?.details?.schema}
                        </DText>
                    </Flex>
                </DetailRow>

                {transaction?.status === 'processing' ? (
                    <>
                        {/* WALLET */}
                        <DetailRow>
                            <DText main>
                                <Text tid={'wallet-address'} />
                            </DText>

                            <div className='flex flex-col items-end justify-start gap-3'>
                                <QrWrapper style={{ marginTop: '10px' }}>
                                    <QrBody>
                                        <QRCode
                                            style={{ width: '144px', height: '144px' }}
                                            value={transaction?.wallet?.address || ''}
                                        />
                                    </QrBody>
                                </QrWrapper>
                                <div className='space-y-2 rounded-xl border px-3 py-2 text-center'>
                                    <DText primary lineBreak={'anywhere'} className='flex flex-row-reverse items-center gap-1'>
                                        :Amount  {" "}
                                        <span className='font-bold'>
                                            {transaction?.meta?.transaction?.source?.amount}
                                        </span>
                                        <IoCopy style={{ marginTop: '-4px' }} size={20} onClick={() => copyToClip(transaction?.meta?.transaction?.source?.amount)} />
                                    </DText>
                                    <DText primary lineBreak={'anywhere'} className='flex flex-row-reverse items-center gap-1'>
                                        :Address {" "}
                                        <span className='font-bold flex flex-row-reverse items-center gap-1'>
                                            {transaction?.wallet?.address}
                                        </span>
                                        <IoCopy style={{ marginTop: '-4px' }} size={20} onClick={() => copyToClip(transaction?.wallet?.address)} />

                                    </DText>
                                </div>
                            </div>
                        </DetailRow>
                    </>
                ) : null}

                {/* DATE */}
                <Flex fw justify={'space-between'} style={{ marginTop: '20px' }}>
                    <DText main>
                        {formatDate(transaction?.createdAt, 'date', lang)}
                    </DText>
                    <DText main>
                        {formatDate(transaction?.createdAt, 'time', lang)}
                    </DText>
                </Flex>

                {/* MODAL */}
                <ModalLayout open={modal.open} width={'520px'} onClose={onModalClose}>
                    <TransactionModal details={modal} onClose={handleOnCloseModal} />
                </ModalLayout>
            </DetailsLayout>

            {
                transaction?.recipientId &&
                <div >
                    <RecipientDetails detailId={transaction?.recipientId} />
                </div>
            }
        </>
    )
}

export default DueTransactionDetails


const QrBody = styled.div`
  border-radius: 4px;
  padding: 4px;
`